import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import type { LocaleEnum } from '@/api/types/auth';

import enGB from './en-GB.json';
import enIN from './en-IN.json';
import enUS from './en-US.json';
import ja from './ja.json';
import {
  DEFAULT_LOCALE_TAG,
  LOCALE_STORAGE_KEY,
  type LocaleTag,
  SUPPORTED_LOCALE_TAGS,
  isSupportedTag,
  localeEnumToTag,
} from './locale';

const resources = {
  'en-GB': { translation: (enGB as { translation: Record<string, unknown> }).translation },
  'en-IN': { translation: (enIN as { translation: Record<string, unknown> }).translation },
  'en-US': { translation: (enUS as { translation: Record<string, unknown> }).translation },
  ja: { translation: (ja as { translation: Record<string, unknown> }).translation },
};

/**
 * Pre-auth boot cache. The User row's locale (server-side) is the source of
 * truth; localStorage exists only so i18next can initialise synchronously
 * before `GET /user` resolves, avoiding a flash of wrong-language UI on every
 * reload. It is reconciled to user.locale once authentication completes.
 */
function readBootCache(): LocaleTag {
  const cached = window.localStorage?.getItem(LOCALE_STORAGE_KEY);
  return isSupportedTag(cached) ? cached : DEFAULT_LOCALE_TAG;
}

const bootLocale = readBootCache();
i18n.use(initReactI18next).init({
  defaultNS: 'translation',
  fallbackLng: DEFAULT_LOCALE_TAG,
  interpolation: {
    escapeValue: false,
  },
  lng: bootLocale,
  resources,
  supportedLngs: [...SUPPORTED_LOCALE_TAGS],
});

/**
 * Reconcile i18next + boot cache against the authoritative `user.locale` from
 * the backend. Call after `GET /user` returns. If user.locale is null
 * (first-run), no-op — the gate will set it explicitly.
 */
export function reconcileLocaleFromUser(userLocale: LocaleEnum | null): void {
  if (!userLocale) {
    return;
  }
  const tag = localeEnumToTag(userLocale);
  if (i18n.language !== tag) {
    void i18n.changeLanguage(tag);
  }
  if (window.localStorage?.getItem(LOCALE_STORAGE_KEY) !== tag) {
    window.localStorage?.setItem(LOCALE_STORAGE_KEY, tag);
  }
}

export default i18n;
